import React from 'react';
import classNames from 'classnames';
import { Grid, Paper, Typography, colors, withStyles } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import CheckedIcon from '@material-ui/icons/CheckBox';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';

const styles = theme => {
  return {
    root: {
      backgroundColor: theme.palette.grey['300'],
      padding: theme.spacing.unit * 1.5,
      width: 'fit-content',
      boxSizing: 'border-box',
    },
    container: {
      gap: `${theme.spacing.unit}px`,
      flexWrap: 'nowrap',
    },
    iconContainer: {
      width: 'fit-content',
    },
    fullWidth: {
      width: '100%',
    },
    content: {
      fontWeight: 500,
      lineHeight: 1,
    },
    default: {
      color: '#000000',
    },
    info: {
      backgroundColor: colors.blue['50'],
      color: colors.blue['800'],
    },
    warning: {
      backgroundColor: colors.yellow['100'],
      '& svg': {
        color: colors.yellow['800'],
      },
    },
    success: {
      backgroundColor: colors.green['50'],
      color: colors.green['800'],
    },
    error: {
      backgroundColor: colors.red['50'],
      color: colors.red['800'],
    },
  };
};

const icons = {
  info: <InfoIcon data-testid="infoPanel-icon-info" />,
  warning: <WarningIcon data-testid="infoPanel-icon-warning" />,
  success: <CheckedIcon data-testid="infoPanel-icon-success" />,
  error: <CancelIcon data-testid="infoPanel-icon-error" />,
};

/**
 * InfoPanel component
 * @param {Object} props - component props
 * @param {('default'|'info'|'warning'|'success'|'error')} props.type - type of the panel
 * @param {string|Element} props.content - content or element to display
 * @param {boolean} props.fullWidth - whether the panel should be full width
 * @param {boolean} props.alignIcon - whether the icon should be aligned to the content
 */
const InfoPanel = ({ type = 'default', content, fullWidth, classes, alignIcon }) => {
  return (
    <Paper
      className={classNames(classes.root, classes[type], {
        [classes.fullWidth]: fullWidth,
      })}
      elevation={0}
      data-testid="infoPanel"
    >
      <Grid container className={classes.container}>
        <Grid item container className={classes.iconContainer} alignItems={alignIcon ? 'center' : 'flex-start'}>
          {icons[type]}
        </Grid>
        <Grid item container alignContent="flex-end">
          {typeof content === 'string' && (
            <Typography variant="body2" className={classNames(classes.text, classes[type])}>
              {content}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

InfoPanel.propTypes = {
  type: PropTypes.oneOf(['default', 'info', 'warning', 'success', 'error']),
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  fullWidth: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string,
    container: PropTypes.string,
    iconContainer: PropTypes.string,
    fullWidth: PropTypes.string,
    content: PropTypes.string,
    default: PropTypes.string,
    info: PropTypes.string,
    warning: PropTypes.string,
    success: PropTypes.string,
    error: PropTypes.string,
  }).isRequired,
  alignIcon: PropTypes.bool,
};

export default withStyles(styles)(InfoPanel);
